import { useSupplyTrash } from '../trash'
import { useSupplyUiConfig } from '../invoice-fields'
import React, { useState } from 'react'
import dayjs from 'dayjs'
import { Button, Space, Tag, Tooltip, Dropdown, message } from 'antd'
import { FilePdfOutlined, FileSearchOutlined, ReloadOutlined, LoadingOutlined, MoreOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { DeliveryAnalysis } from './model'
import { analysisLabel, analysisPath, savedAnalysisState } from './analysis'
import { supplyRequest } from './api'
import ProcessingProgressIndicator from '../processing-progress-indicator'

export default function SupplierFiles({ analyses, refresh }: { analyses: DeliveryAnalysis[]; refresh: () => void }) {
  const navigate = useNavigate()
  const currentUserId = useSelector((state: any) => state.session?.sessionData?.user?.id)
  const uiConfig = useSupplyUiConfig()
  const deletion = useSupplyTrash(refresh)
  const [busy, setBusy] = useState('')
  const retry = async (row: DeliveryAnalysis, refine = false) => {
    setBusy(row.review_id)
    try {
      const result = await supplyRequest(`/api/supply/processing/jobs/${row.processing_job_id}/${refine ? 'refine' : 'retry'}`, { method: 'POST' })
      message.success(result.data.state === 'ready' ? 'Готовый результат сохранён' : refine ? 'Уточнение полей поставлено в очередь' : 'Файл поставлен в очередь')
      refresh()
    } catch (cause: any) { message.error(cause.message) }
    finally { setBusy('') }
  }
  if (!analyses.length) return null
  return <div className="delivery-calendar__files" aria-label="Загруженные документы поставщика">
    {deletion.contextHolder}
    {analyses.map(row => {
      const state = savedAnalysisState(row), label = analysisLabel(state)
      const files = row.files?.length ? row.files : [{ name: 'Документы поставки' }]
      return <div className="delivery-calendar__file" key={row.review_id}>
        <FilePdfOutlined className="delivery-calendar__file-icon" />
        <div className="delivery-calendar__file-info">
          <strong>{files.map(file => file.name).join(', ')}</strong>
          {(row.created_at || (row.attempt || 0) > 1) && <small>
            {[row.created_at && dayjs(row.created_at).isValid() ? dayjs(row.created_at).format('DD.MM · HH:mm') : '',
              (row.attempt || 0) > 1 ? `Повторная попытка ${row.attempt}` : ''].filter(Boolean).join(' · ')}
          </small>}
          {row.documents?.map(document => <Button type="link" size="small" key={document.id} icon={<FileSearchOutlined />}
            onClick={() => navigate(analysisPath(row.review_id, { ...state, documentId: document.id }))}>
            {document.number ? `Открыть накладную № ${document.number}` : 'Открыть накладную'} · {document.lines} строк
          </Button>)}
          {row.processing_error && <small className={state.status === 'failed' ? 'delivery-calendar__file-error' : ''}>{row.processing_error}</small>}
          {row.has_unread_fields && state.status === 'review' && <small>Есть непрочитанные поля — проверьте по оригиналу</small>}
        </div>
        <Space wrap className="delivery-calendar__file-actions">
          {row.documents?.map(document => {
            const needsReview = state.status === 'review' && document.status !== 'confirmed'
            const number = document.number ? ` № ${document.number}` : ''
            return <Button key={document.id} type={needsReview ? 'primary' : 'default'} icon={<EyeOutlined />}
              className="delivery-calendar__file-open" aria-label={`${needsReview ? 'Проверить' : 'Открыть'} накладную${number}`}
              onClick={() => navigate(analysisPath(row.review_id, { ...state, documentId: document.id }))}>
              {needsReview ? 'Проверить накладную' : 'Открыть накладную'}{row.documents!.length > 1 && number}
            </Button>
          })}
          {(row.processing_job_id || row.documents?.length) && (uiConfig.can_open_chat || Number(row.created_by) === Number(currentUserId)) && <Dropdown trigger={['click']} menu={{ items: [
            ...(row.processing_job_id ? [{ key: 'delete-file', danger: true, icon: <DeleteOutlined />, label: 'Удалить загрузку', onClick: () => deletion.change({ kind: 'job', id: row.processing_job_id!, name: files.map(file => file.name).join(', ') }) }] : []),
            ...(row.documents || []).map(document => ({ key: document.id, danger: true, icon: <DeleteOutlined />, label: `Удалить накладную ${document.number || 'без номера'}`, onClick: () => deletion.change({ kind: 'document', id: document.id, name: `Накладная № ${document.number || 'без номера'}` }) })),
          ] }}><Button type="text" size="small" icon={<MoreOutlined />} aria-label={`Действия с документами ${files.map(file => file.name).join(', ')}`} /></Dropdown>}
          {state.status === 'processing' && row.processing_progress
            ? <div className="delivery-calendar__file-progress">
              <div className="delivery-calendar__file-progress-title"><LoadingOutlined /><span>{label.text}</span></div>
              <ProcessingProgressIndicator progress={row.processing_progress} />
            </div>
            : <Tag color={label.color} icon={state.status === 'processing' ? <LoadingOutlined /> : undefined}>{label.text}</Tag>}
          {state.status === 'review' && row.has_unread_fields && !row.recognition_repair_count && row.processing_job_id && Number(row.created_by) === Number(currentUserId) &&
            <Tooltip title="Повторно прочитаем только пустые обязательные поля. Поиск товаров в 1С не повторяется.">
              <Button size="small" icon={<FileSearchOutlined />} loading={busy === row.review_id} onClick={() => retry(row, true)}>Уточнить поля</Button>
            </Tooltip>}
          {state.status === 'failed' && row.processing_job_id && Number(row.created_by) === Number(currentUserId) &&
            <Tooltip title="Сначала проверим готовый ответ агента. Успешные файлы заново не запускаются.">
              <Button size="small" icon={<ReloadOutlined />} loading={busy === row.review_id} onClick={() => retry(row)}>Проверить / повторить</Button>
            </Tooltip>}
        </Space>
      </div>
    })}
  </div>
}
